import { useState } from 'react'
import { useAuth } from '../store/useAuth'

/**
 * THE FRONT DOOR.
 *
 * Nothing behind this renders without a session. Not the canvas, not a saved
 * job, not the quote screen. That is what "they need an account to use it"
 * means, and doing it any other way - drawing first, signing in to save -
 * means maintaining two paths forever and explaining to somebody why the
 * house they drew before signing in is not in their account.
 *
 * SIGNING UP HAPPENS ON THE SITE, not here. The account, the trial and the
 * card all live with billing, and a second signup form on this side would be
 * a second place a person can exist. One door in, one place accounts are made.
 *
 * The failure text says the same thing for a wrong password as for an address
 * that has no account. Telling those apart tells a stranger whose email is
 * registered.
 *
 * TWO WAYS IN, AND THE LINK IS THE DEFAULT.
 *
 * A password is a thing to lose, and losing it is what happens on the day you
 * need the app in a driveway. Email me a link needs nothing remembered and
 * nothing reset: Supabase mails a one-time link, following it opens a session,
 * and the session is what stamps the account onto this browser. The password
 * form is still here for anybody who wants it.
 */

import { BRAND, SITE } from '../lib/brand'

export default function SignIn() {
  const signIn = useAuth((s) => s.signIn)
  const sendLink = useAuth((s) => s.sendLink)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [usePassword, setUsePassword] = useState(false)
  const [busy, setBusy] = useState(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setErr(null)

    if (!usePassword) {
      const message = await sendLink(email)
      setErr(message)
      /*
       * Sent is claimed whether the address had an account or not, for the
       * same reason the password error is one message: an inbox that stays
       * empty tells the owner of the inbox, and nobody else.
       */
      if (!message) setSent(true)
      setBusy(false)
      return
    }

    const message = await signIn(email, password)
    setErr(message)
    setBusy(false)
  }

  return (
    <div className="gate">
      <form className="gate-card" onSubmit={submit}>
        {/*
          THE DARK MARK, BECAUSE THE DOOR IS LIGHT NOW.

          This screen was near black with a glass card on it, which is the
          editor's chrome - and the editor is dark for one reason, that a warm
          cast beside a night render makes you misjudge the render. There is no
          photograph on a login. So it wears the same light ground as the
          workspace it opens into, and it stops being a black flash before a
          light app.
        */}
        <img className="gate-logo" src="lightsos-dark.webp" alt={BRAND} />

        <h1>Sign in</h1>
        <p className="gate-note">Your jobs, your quotes and your season, on whatever you are holding.</p>

        <label>
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>

        {usePassword && (
          <label>
            <span>Password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
        )}

        {err && <p className="gate-err">{err}</p>}

        {/*
          WHAT HAPPENS NEXT, SAID WHERE IT HAPPENED. A link that has been sent
          is the whole of the screen's state, and the next move is in an inbox
          rather than on this page, so the page says so and stops asking.
        */}
        {sent && !err ? (
          <p className="gate-sent">
            Check {email} for a link. It signs you straight in and it only
            works once.
          </p>
        ) : (
          <button className="btn primary" type="submit" disabled={busy}>
            {busy
              ? usePassword
                ? 'Signing in...'
                : 'Sending...'
              : usePassword
                ? 'Sign in'
                : 'Email me a link'}
          </button>
        )}

        <p className="gate-alt">
          <button
            type="button"
            className="gate-swap"
            onClick={() => {
              setUsePassword((v) => !v)
              setErr(null)
              setSent(false)
            }}
          >
            {usePassword ? 'Email me a link instead' : 'Use a password instead'}
          </button>
          <span> &middot; </span>
          <a href={`${SITE}/#pricing`}>Start a free trial</a>
        </p>
      </form>
    </div>
  )
}
